import type { Pog } from "@CommonTypes/merchflow/pog/pog";
import { PlanogramItem } from "@CommonTypes/merchflow/pog/item";
import {
  DEFAULT_SECTION_ATTR,
  itemMasterIdentifierOf,
  type PogAdapterOptions,
  type PogSectionSemantics,
} from "@CommonUtils/cdtStudio/adapters/pogToStudioItems";

/** The adapter's own missing-value spellings, used when the options name none. */
const ADAPTER_MISSING_VALUES = ["na", "n/a", "n-a", "none", "unknown", "unspecified"];
const POG_COLUMNS = ["subCategory", "brand", "cdt1", "cdt2", "cdt3", "variant", "size"] as const;

const textOf = (value: unknown): string =>
  String(value ?? "")
    .trim()
    .toLowerCase();

/** The adapter's section → condition → fallback precedence for one product. */
const governingMapping = (
  semantics: readonly PogSectionSemantics[],
  section: string,
  attrs: Readonly<Record<string, string | null>>,
): PogSectionSemantics | undefined => {
  let fallback: PogSectionSemantics | undefined;
  for (const mapping of semantics) {
    if (mapping.sections.length === 0) {
      fallback ??= mapping;
      continue;
    }
    if (!mapping.sections.some((name) => textOf(name) === section)) continue;
    const matches = Object.entries(mapping.when ?? {}).every(([column, values]) =>
      values.some((value) => textOf(value) === (attrs[column] ?? "")),
    );
    if (matches) return mapping;
  }
  return fallback;
};

/**
 * A profile's section mapping says what each planogram CDT column means ("CDT 2 is brand").
 * Where the item master carries that attribute itself, the mapping is checked against it: a
 * column that agrees with the live attribute on no product the mapping governs (missing values
 * aside) is a different attribute, and the mapping is dropped so the live values stand.
 *
 * Sam's Club laundry's saved profile says CDT 2 is brand; on its base planogram CDT 2 holds
 * the pack format (Liquid, Unit dose), and the item master's brand (Tide, Gain) agrees with it
 * on no product. Written over the live brand, the Brand level showed pack formats. A mapping
 * that overrides only some products (deodorant's "brand block" reads CDT 1, which matches the
 * item master's brand block on 207 of 253 products) agrees somewhere, and stands.
 */
export const withLiveSectionSemantics = (
  pogs: readonly Pog[],
  options: PogAdapterOptions,
): PogAdapterOptions => {
  const semantics = options.sectionSemantics;
  const overlay = options.attributeOverlay;
  if (!semantics || semantics.length === 0 || !overlay) return options;
  const spellings =
    options.normalization === false
      ? false
      : (options.normalization?.placeholderSpellings ?? ADAPTER_MISSING_VALUES);
  const missing = new Set(spellings === false ? [] : spellings.map(textOf));
  // As the adapter reads a value: blank is absent, every missing spelling is one Unspecified.
  const valueOf = (value: unknown): string | null => {
    const text = textOf(value);
    return text === "" ? null : missing.has(text) ? "unspecified" : text;
  };
  const comparable = (value: unknown): string | null => {
    const text = valueOf(value);
    return text === "unspecified" ? null : text;
  };
  const sectionAttr = options.sectionAttr ?? DEFAULT_SECTION_ATTR;
  /* Each product with the attributes the adapter resolves its mapping from (POG columns, the
     item master over them), filed under the one mapping that governs it. */
  const governed = new Map<
    PogSectionSemantics,
    { live: Record<string, string | null> | undefined; attrs: Record<string, string | null> }[]
  >();
  for (const pog of pogs) {
    for (const bay of pog.planogram.bays) {
      for (const shelf of bay.shelves) {
        for (const item of shelf.items) {
          if (!(item instanceof PlanogramItem)) continue;
          const live =
            overlay[
              options.itemIdentifierByProductCode?.[item.productCode || item.id] ??
                itemMasterIdentifierOf(item, options.itemIdentifier)
            ];
          const attrs: Record<string, string | null> = Object.fromEntries(
            Object.entries({
              ...Object.fromEntries(POG_COLUMNS.map((column) => [column, item[column]])),
              ...live,
            }).map(([column, value]) => [column, valueOf(value)]),
          );
          // As pogToStudioItems does: the item master fills a missing CDT0 but never overwrites one.
          const pogCdt0 = comparable(item.variant);
          if (pogCdt0 !== null) attrs.variant = pogCdt0;
          const mapping = governingMapping(semantics, attrs[sectionAttr] ?? "", attrs);
          if (!mapping) continue;
          if (!governed.has(mapping)) governed.set(mapping, []);
          governed.get(mapping)?.push({ live, attrs });
        }
      }
    }
  }
  const contradicted = (mapping: PogSectionSemantics, column: string, semantic: string) => {
    let compared = 0;
    for (const { live, attrs } of governed.get(mapping) ?? []) {
      const target = comparable(live?.[semantic]);
      if (target === null) continue;
      const source = comparable(attrs[column]);
      if (source === null) continue;
      if (source === target) return false;
      compared += 1;
    }
    return compared > 0;
  };
  let changed = false;
  const checked = semantics.map((mapping) => {
    const columns = Object.fromEntries(
      Object.entries(mapping.columns).filter(
        ([column, semantic]) => column === semantic || !contradicted(mapping, column, semantic),
      ),
    );
    if (Object.keys(columns).length === Object.keys(mapping.columns).length) return mapping;
    changed = true;
    return { ...mapping, columns };
  });
  return changed ? { ...options, sectionSemantics: checked } : options;
};
